import { useSchoolQuery } from './useSchoolQuery';

export interface LevelOption {
  /** Classroom ID — bound by `<Select v-model>` in level dropdowns. */
  value: string;
  /** Display label, e.g. "100 Level". */
  label: string;
  /** Parsed integer (from Classroom.name) — what the BE schema expects today. */
  level: number;
}

// Tertiary "levels" (100, 200, …) are stored as Classroom records on the backend.
// Dropdown values are `classroom_id` so the UI is keyed on the real domain entity;
// consumers that still call BE endpoints typed as `level: Int` use `levelForId()` to
// resolve the integer at call time.
export const useTertiaryLevels = () => {
  const { classrooms, loadingSchool } = useSchoolQuery();

  const levelOptions = computed<(LevelOption & { classroomName: string })[]>(() => {
    const items = (classrooms.value ?? [])
      .map((c) => {
        if (!c || !c.name?.trim()) return null;
        const parsed = parseInt(c.name, 10);
        const level = isNaN(parsed) ? 0 : parsed;
        return {
          value: String(c.id),
          label: `${c.name} Level`,
          level,
          classroomName: c.name,
          sequence: c.sequence ?? level,
        };
      })
      .filter((x): x is LevelOption & { classroomName: string; sequence: number } => x !== null);
    items.sort((a, b) => a.sequence - b.sequence);
    return items.map(({ value, label, level, classroomName }) => ({ value, label, level, classroomName }));
  });

  const levelForId = (classroomId: string | null | undefined): string | null => {
    if (!classroomId) return null;
    const opt = levelOptions.value.find((o) => o.value === String(classroomId));
    return opt?.classroomName ?? null;
  };

  const levelForSectionId = (sectionId: string | null | undefined): string | null => {
    if (!sectionId) return null;
    const match = (classrooms.value ?? []).find((c) =>
      c?.classroomSections?.some((cs: { id: string | number }) => String(cs.id) === String(sectionId)),
    );
    return match?.name ?? null;
  };

  // Permission scopes persist the level number itself (e.g. "100") rather than
  // a classroom_id, so existing grants stay comparable with `registration_level`.
  const levelOptionsAsString = computed<{ label: string; value: string }[]>(() =>
    levelOptions.value.map(({ label, classroomName }) => ({ label, value: classroomName })),
  );

  return {
    levelOptions,
    levelForId,
    levelForSectionId,
    levelOptionsAsString,
    loadingLevels: loadingSchool,
  };
};
